/* eslint-disable jsx-a11y/anchor-is-valid */
// src/pages/seller/Seller2Net/components/PropertyDetailsSection.js
import React, { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
// import FloatingInput from "../../../../components/input/FloatingInput";
import FloatingNumber from "../../../../components/input/FloatingNumber";
import ControlledField from "./ControlledField";

const RadioGroup = React.memo(({ name, value, onChange, options }) => (
  <div className="frm-radio">
    {options.map((opt) => (
      <label key={opt.value}>
        <input
          type="radio"
          name={name}
          value={opt.value}
          checked={value === opt.value}
          onChange={() => onChange(opt.value)}
        />{" "}
        {opt.label}
      </label>
    ))}
  </div>
));

function PropertyDetailsSection({
  ttbData,
  formatCurrency,
  isCorrectProperty,
  setIsCorrectProperty,
  useAnnualPropertyTax,
  setUseAnnualPropertyTax,
  salesPriceError,
  purchasePriceError,
  formData,
  setFormData,
  handleCurrencyBlur,
  handleCalculate,
}) {
  // Setup react-hook-form
  const { control, setValue } = useForm({
    defaultValues: {
      customAssessedValueAmount: formData.loanInfoMeta?.assessedValueAmount || "0.00",
    }
  });

  // Local state for custom assessed value input
  const [customAssessedValue, setCustomAssessedValue] = useState(
    formData.loanInfoMeta?.assessedValueAmount || "0.00"
  );

  // Sync local state with formData changes
  React.useEffect(() => {
    const currentValue = formData.loanInfoMeta?.assessedValueAmount || "0.00";
    if (currentValue !== customAssessedValue) {
      setCustomAssessedValue(currentValue);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- customAssessedValue intentionally excluded to avoid sync loop
  }, [formData.loanInfoMeta?.assessedValueAmount]);

  // Clean number utility function
  const cleanNumber = (v) => {
    if (v === undefined || v === null) return "";
    const s = typeof v === "string" ? v : String(v);
    return s.replace(/[^0-9.]/g, "");
  };

  // Error state for assessed value amount
  const assessedValueAmountError = "";

  // Memoize derived values for cleaner render
  const assessedValue = useMemo(
    () => Number(ttbData?.assessedValue || 0),
    [ttbData]
  );

  const handleSalesPriceChange = useCallback(
    (e) => setFormData((p) => ({ ...p, salesPrice: e.target.value })),
    [setFormData]
  );
  const handlePurchasePriceChange = useCallback(
    (e) => setFormData((p) => ({ ...p, purchasePrice: e.target.value })),
    [setFormData]
  );

  return (
    <div className="property-detail-container">
      <div className="property-details-list">
        {/* ✅ Correct property question */}
        <div className="property-question">
          <div className="content-description">
            <p className="heading-d txtclr">Is this the correct property? </p>
          </div>
          <div className="form-value">
            <RadioGroup
              name="correctproperty"
              value={isCorrectProperty}
              onChange={setIsCorrectProperty}
              options={[
                { label: "Yes", value: true },
                { label: "No", value: false },
              ]}
            />
          </div>
        </div>

        {isCorrectProperty && (
          <>
            {/* ✅ Annual Property Tax */}
            {assessedValue > 0 && (
              <>
                <div className="property-question p-t-20 m-t-20 border-top-sec">
                  <div className="content-description">
                    <p className="heading-f txtclr">
                      We see the Current Home - Tax Assessed Value on this
                      property is{" "}
                      <button>${formatCurrency(ttbData.assessedValue)}</button>.
                    </p>
                  </div>
                </div>

                <div className="property-question m-t-20">
                  <div className="content-description">
                    <p className="heading-b txtclr">
                      Do you want to use this amount in the calculations?
                    </p>
                  </div>
                  <div className="form-value">
                    <RadioGroup
                      name="annualPropertyTaxUse"
                      value={useAnnualPropertyTax}
                      onChange={setUseAnnualPropertyTax}
                      options={[
                        { label: "Yes", value: true },
                        { label: "No", value: false },
                      ]}
                    />
                  </div>
                </div>

                {/* ✅ Custom Amount Input - Only show when user selects "No" */}
                {!useAnnualPropertyTax && (
                  <div className="property-question m-t-20">
                    <div className="content-description">
                      <p className="heading-b txtclr">
                        What amount do you want to use in the calculations?
                      </p>
                    </div>
                    <div className="form-value">
                      <div className="form-group sign-of-amount">
                        <ControlledField
                          name="customAssessedValueAmount"
                          control={control}
                          defaultValue={formData.loanInfoMeta?.assessedValueAmount || "0.00"}
                          InputComponent={FloatingNumber}
                          placeholder="Enter custom amount"
                          type="text"
                          cleanNumber={cleanNumber}
                          handleCurrencyBlur={handleCurrencyBlur}
                          onFormatted={(name, formatted) => {
                            try {
                              setValue &&
                                setValue(
                                  "customAssessedValueAmount",
                                  formatted
                                );
                            } catch (err) {
                              console.warn(
                                "Failed to sync custom amount",
                                err
                              );
                            }

                            try {
                              setFormData((prev) => {
                                const updated = {
                                  ...prev,
                                  loanInfoMeta: {
                                    ...(prev.loanInfoMeta || {}),
                                    assessedValueAmount: formatted,
                                  },
                                };

                                
                                return updated;
                              });
                            } catch (e) {
                              console.warn("Failed to update formData", e);
                            }
                          }}
                        />
                        {assessedValueAmountError && (
                          <div className="text-red-600 text-sm mt-1">
                            {assessedValueAmountError}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

              </>
            )}

            {/* ✅ Sales Price */}
            <div className="property-question p-t-20 m-t-20 border-top-sec">
              <div className="content-description">
                <p className="heading-f txtclr">Sales price details</p>
              </div>
            </div>

            <div className="property-question m-t-20">
              <div className="content-description">
                <p className="heading-b txtclr">
                  What is the Current Home - Sales Price?
                </p>
              </div>
              <div className="form-value">
                <div className="form-group sign-of-amount">
                  <FloatingNumber
                    type="text"
                    name="salesPrice"
                    placeholder="0.00"
                    className="inpt-pla"
                    value={formData.salesPrice || ""}
                    onChange={handleSalesPriceChange}
                    onBlur={() => handleCurrencyBlur("salesPrice")}
                  />
                  {salesPriceError && (
                    <div className="text-red-600 text-sm mt-1">
                      {salesPriceError}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="property-question p-t-20 m-t-20 border-top-sec">
              <div className="content-description">
                <p className="heading-f txtclr">Purchase price details</p>
              </div>
            </div>
            <div className="property-question m-t-20">
              <div className="content-description">
                <p className="heading-b txtclr">
                  What is the expected New Home - Purchase Price?
                </p>
              </div>
              <div className="form-value">
                <div className="form-group sign-of-amount">
                  <FloatingNumber
                    type="text"
                    name="purchasePrice"
                    placeholder="0.00"
                    className="inpt-pla"
                    value={formData.purchasePrice || ""}
                    onChange={handlePurchasePriceChange}
                    onBlur={() => handleCurrencyBlur("purchasePrice")}
                  />
                  {purchasePriceError && (
                    <div className="text-red-600 text-sm mt-1">
                      {purchasePriceError}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ✅ Calculate Button */}
            <div className="middle-content-calc calculate-btn">
              <button
                className="submit-btn btn-bg inpt-btn"
                onClick={handleCalculate}
              >
                Calculate
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default React.memo(PropertyDetailsSection);
